import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./components/theme-provider";
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import LecturePage from "./pages/LecturePage";
import QAPage from "./pages/QAPage";
import SettingsPage from "./pages/SettingsPage";
import SubjectsPage from "./pages/SubjectsPage";
import LibraryPage from "./pages/LibraryPage";
import NotesPage from "./pages/NotesPage";
import MasteryJourneyPage from "./pages/MasteryJourneyPage";
import SharedPreviewPage from "./pages/SharedPreviewPage";
import ReviewPage from "./pages/ReviewPage";
import QnAReviewPage from "./pages/QnAReviewPage";
import ExamSchedulePage from "./pages/ExamSchedulePage";
import ProgressPage from "./pages/ProgressPage";
import AppShell from "./components/AppShell";
import TeacherAvatar from "./components/TeacherAvatar";
import { Loader2 } from "lucide-react";
import { useTheme } from "next-themes";

const AuthLoading = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const ProtectedLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <AppShell />;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <AuthLoading />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const ThemeSync = () => {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    if (!user?.appearance_mode) return;
    if (user.appearance_mode !== theme) {
      setTheme(user.appearance_mode);
    }
  }, [user?.appearance_mode, theme, setTheme]);

  return null;
};

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback) - synchronous check
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected app shell */}
      <Route element={<ProtectedLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/lecture" element={<LecturePage />} />
        <Route path="/lecture/:id" element={<LecturePage />} />
        <Route path="/qa" element={<QAPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/subjects" element={<SubjectsPage />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/notes" element={<NotesPage />} />
        <Route path="/notes/:id" element={<NotesPage />} />
        <Route path="/mastery" element={<MasteryJourneyPage />} />
        <Route path="/shared/:kind/:id" element={<SharedPreviewPage />} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/review/:id" element={<ReviewPage />} />
        <Route path="/qna-review" element={<QnAReviewPage />} />
        <Route path="/qna-review/:id" element={<QnAReviewPage />} />
        <Route path="/progress" element={<ProgressPage />} />
        <Route path="/exams" element={<ExamSchedulePage />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemeSync />
          <AppRouter />
          <TeacherAvatar />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
